import { useEffect, useState } from 'react';
import { ArrowLeft, Users, CheckCircle, AlertTriangle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../../services/apiService';
import { BrandSpinner } from '../../common/LoadingSpinner';

type Step = 'list' | 'join' | 'home' | 'success' | 'error';
type JoinScreen = 'landing' | 'constitution' | 'sponsors' | 'confirm';

function purposeChapters(preview: any) {
  const all = Array.isArray(preview?.chapters) ? preview.chapters : [];
  const extra = all.filter((c: any) => c.code !== 'main');
  return extra.length ? extra : all;
}

export function ClubsOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const inviteToken = params.get('invite') || '';
  const [step, setStep] = useState<Step>('list');
  const [loading, setLoading] = useState(true);
  const [clubs, setClubs] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [amount, setAmount] = useState('');
  const [advanceAmount, setAdvanceAmount] = useState('20');
  const [advanceKind, setAdvanceKind] = useState<'airtime' | 'data'>('airtime');
  const [advanceMsisdn, setAdvanceMsisdn] = useState('');
  const [ncaAttested, setNcaAttested] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [joinScreen, setJoinScreen] = useState<JoinScreen>('landing');
  const [adoptedRules, setAdoptedRules] = useState(false);
  const [proposerMsisdn, setProposerMsisdn] = useState('');
  const [seconderMsisdn, setSeconderMsisdn] = useState('');
  const [chapterIds, setChapterIds] = useState<number[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        if (inviteToken) {
          const data = await apiService.previewClubInvite(inviteToken);
          setPreview(data);
          setChapterIds(purposeChapters(data).map((c: any) => Number(c.id)).filter(Boolean));
          setJoinScreen('landing');
          setStep('join');
        } else {
          const data = await apiService.listMyClubs();
          setClubs(Array.isArray(data) ? data : []);
        }
      } catch (err: any) {
        setError(err?.message || 'Could not load clubs.');
        setStep('error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [inviteToken]);

  const openClub = async (clubId: number) => {
    setLoading(true);
    try {
      const home = await apiService.getClubHome(clubId);
      setSelected(home);
      if (home?.club?.contributionAmountZar) {
        setAmount(String(home.club.contributionAmountZar));
      }
      setStep('home');
    } catch (err: any) {
      setError(err?.message || 'Could not open club.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const lookupCode = async () => {
    setLoading(true);
    try {
      const data = await apiService.previewClubCode(code.trim().toUpperCase());
      setPreview(data);
      setChapterIds(purposeChapters(data).map((c: any) => Number(c.id)).filter(Boolean));
      setJoinScreen('landing');
      setStep('join');
    } catch (err: any) {
      setError(err?.message || 'Club code not found.');
    } finally {
      setLoading(false);
    }
  };

  const sponsorBody = () => ({
    popiaConsentVersion: '1',
    chapterIds,
    ...(preview?.requireProposerSeconder ? {
      proposerMsisdn: proposerMsisdn.trim(),
      seconderMsisdn: seconderMsisdn.trim(),
    } : {}),
  });

  const accept = async () => {
    setLoading(true);
    try {
      if (inviteToken) {
        await apiService.acceptClubInvite(inviteToken, sponsorBody());
      } else {
        await apiService.joinClubByCode(preview.clubCode || code, sponsorBody());
      }
      setMessage(
        preview?.requireCommitteeApproveJoin
          ? 'The committee will review your request. You will see the club here once they approve.'
          : 'You have joined the club. Contribute from your wallet when you are ready.'
      );
      setStep('success');
    } catch (err: any) {
      setError(err?.message || 'Could not join this club.');
    } finally {
      setLoading(false);
    }
  };

  const advanceJoin = () => {
    setError('');
    if (joinScreen === 'landing') {
      setJoinScreen('constitution');
      return;
    }
    if (joinScreen === 'constitution') {
      if (!adoptedRules) {
        setError('Tick to confirm you adopt these club rules.');
        return;
      }
      setJoinScreen(preview?.requireProposerSeconder ? 'sponsors' : 'confirm');
      return;
    }
    if (joinScreen === 'sponsors') {
      if (!proposerMsisdn.trim() || !seconderMsisdn.trim()) {
        setError('Enter the mobile numbers of two current members who will propose and second you.');
        return;
      }
      setJoinScreen('confirm');
    }
  };

  const clubPortalHost = typeof window !== 'undefined' && window.location.hostname.includes('staging')
    ? 'https://stagingclubs.mymoolah.africa'
    : 'https://clubs.mymoolah.africa';

  const requestAdvance = async () => {
    if (!selected?.club?.id) return;
    if (!ncaAttested) {
      setError('Tick to confirm the club advances this, not MyMoolah.');
      return;
    }
    setLoading(true);
    try {
      const data = await apiService.requestClubVasAdvance(selected.club.id, {
        amountZar: Number(advanceAmount),
        productKind: advanceKind,
        msisdn: advanceMsisdn,
        ncaAttested: true,
      });
      setMessage(data?.next
        ? `Club VAS advance requested. ${data.next}`
        : `Club VAS advance R${Number(advanceAmount).toFixed(2)} — repay from next contribute. The club advanced this, not MyMoolah.`);
      setStep('success');
    } catch (err: any) {
      setError(err?.message || 'Club VAS advance failed.');
    } finally {
      setLoading(false);
    }
  };

  const repayAdvance = async () => {
    if (!selected?.club?.id || !selected?.vasAdvance?.myOpenAdvanceId) return;
    setLoading(true);
    try {
      await apiService.repayClubVasAdvance(selected.club.id, selected.vasAdvance.myOpenAdvanceId);
      setMessage('Club VAS advance repaid from your wallet.');
      setStep('success');
    } catch (err: any) {
      setError(err?.message || 'Repayment failed.');
    } finally {
      setLoading(false);
    }
  };

  const contribute = async () => {
    if (!selected?.club?.id) return;
    setLoading(true);
    try {
      await apiService.contributeToClub(selected.club.id, Number(amount));
      setMessage(`Contribution of R${Number(amount).toFixed(2)} paid to ${selected.club.name}.`);
      setStep('success');
    } catch (err: any) {
      setError(err?.message || 'Contribution failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: 'Montserrat, sans-serif', padding: '16px', color: '#1f2937' }}>
      <button
        type="button"
        onClick={() => navigate('/transact')}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', color: '#2D8CCA', marginBottom: 16 }}
      >
        <ArrowLeft className="w-5 h-5" /> Back
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Users className="w-6 h-6" color="#86BE41" />
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Clubs</h1>
      </div>

      {loading && <BrandSpinner />}

      {!loading && step === 'list' && (
        <div>
          <p style={{ fontSize: 14, marginBottom: 16 }}>Invitation-only clubs. No platform fee. No promised return.</p>
          {clubs.length === 0 && <p style={{ fontSize: 14 }}>You have not joined a club yet.</p>}
          {clubs.map((club) => (
            <button
              key={club.clubId}
              type="button"
              onClick={() => openClub(club.clubId)}
              style={{ width: '100%', textAlign: 'left', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, marginBottom: 10, background: '#fff', minHeight: 44 }}
            >
              <strong>{club.name}</strong>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Next due {club.nextDueOn || '—'}</div>
            </button>
          ))}
          <label htmlFor="club-code" style={{ display: 'block', fontSize: 13, marginTop: 16 }}>Club code</label>
          <input
            id="club-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={6}
            style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, marginTop: 6 }}
          />
          <button type="button" onClick={lookupCode} style={{ marginTop: 10, background: '#2D8CCA', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px' }}>
            Look up invite
          </button>
        </div>
      )}

      {!loading && step === 'join' && preview && preview.kind === 'chair_setup' && (
        <div>
          <h2 style={{ fontSize: 18 }}>{preview.clubName}</h2>
          <p style={{ fontSize: 14 }}>This link is for the nominated chair. Setup continues on the Club Portal.</p>
          <a href={`${clubPortalHost}/club/setup/${inviteToken}`} style={{ display: 'block', marginTop: 16, background: '#86BE41', color: '#fff', borderRadius: 8, padding: '12px 16px', textAlign: 'center', textDecoration: 'none' }}>
            Continue chair setup
          </a>
        </div>
      )}

      {!loading && step === 'join' && preview && preview.kind !== 'chair_setup' && (
        <div>
          <h2 style={{ fontSize: 18 }}>{preview.clubName}</h2>
          {joinScreen === 'landing' && (
            <div>
              <p style={{ fontSize: 14 }}>Turn on the chapters you will pay. You can only choose among chapters this club has switched on.</p>
              {purposeChapters(preview).map((ch: any) => {
                const on = chapterIds.includes(Number(ch.id));
                return (
                  <label key={ch.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 12, minHeight: 44, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => {
                        const id = Number(ch.id);
                        setChapterIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
                      }}
                    />
                    <span>
                      <strong>{ch.name}</strong>
                      <span style={{ display: 'block', color: '#6b7280' }}>R{Number(ch.contributionAmountZar || 0).toFixed(2)} {ch.contributionFrequency || preview.contributionFrequency}</span>
                    </span>
                  </label>
                );
              })}
              {purposeChapters(preview).length === 0 && (
                <p style={{ fontSize: 14 }}>Contribution R{Number(preview.contributionAmountZar).toFixed(2)} {preview.contributionFrequency}.</p>
              )}
              <p style={{ fontSize: 14, marginTop: 12 }}>
                Total R{purposeChapters(preview)
                  .filter((ch: any) => chapterIds.includes(Number(ch.id)))
                  .reduce((sum: number, ch: any) => sum + Number(ch.contributionAmountZar || 0), 0)
                  .toFixed(2)} this cycle.
              </p>
              <p style={{ fontSize: 13, color: '#6b7280' }}>Invitation only. The committee may still need to approve your join.</p>
              <button type="button" onClick={advanceJoin} style={{ marginTop: 16, background: '#86BE41', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 16px', width: '100%', minHeight: 44 }}>
                Continue
              </button>
            </div>
          )}
          {joinScreen === 'constitution' && (
            <div>
              <p style={{ fontSize: 14, background: '#f8fafc', padding: 12, borderRadius: 8 }}>{preview.summary}</p>
              <p style={{ fontSize: 12, color: '#6b7280' }}>Rules hash {preview.rulesHash || '—'}</p>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 13 }}>
                <input type="checkbox" checked={adoptedRules} onChange={(e) => setAdoptedRules(e.target.checked)} />
                <span>I have read these rules and agree to adopt them.</span>
              </label>
              <button type="button" onClick={advanceJoin} style={{ marginTop: 16, background: '#86BE41', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 16px', width: '100%' }}>
                Continue
              </button>
            </div>
          )}
          {joinScreen === 'sponsors' && (
            <div>
              <p style={{ fontSize: 14 }}>This club needs a proposer and a seconder who are already members.</p>
              <label htmlFor="proposer" style={{ display: 'block', fontSize: 13, marginTop: 12 }}>Proposer mobile</label>
              <input
                id="proposer"
                value={proposerMsisdn}
                onChange={(e) => setProposerMsisdn(e.target.value)}
                inputMode="tel"
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, marginTop: 6 }}
              />
              <label htmlFor="seconder" style={{ display: 'block', fontSize: 13, marginTop: 12 }}>Seconder mobile</label>
              <input
                id="seconder"
                value={seconderMsisdn}
                onChange={(e) => setSeconderMsisdn(e.target.value)}
                inputMode="tel"
                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, marginTop: 6 }}
              />
              <button type="button" onClick={advanceJoin} style={{ marginTop: 16, background: '#86BE41', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 16px', width: '100%' }}>
                Continue
              </button>
            </div>
          )}
          {joinScreen === 'confirm' && (
            <div>
              <p style={{ fontSize: 14 }}>You are asking to join {preview.clubName}.</p>
              <ul style={{ fontSize: 14, paddingLeft: 18 }}>
                {purposeChapters(preview).filter((ch: any) => chapterIds.includes(Number(ch.id))).map((ch: any) => (
                  <li key={ch.id}>{ch.name} · R{Number(ch.contributionAmountZar || 0).toFixed(2)}</li>
                ))}
              </ul>
              {preview.requireCommitteeApproveJoin && (
                <p style={{ fontSize: 13, color: '#6b7280' }}>The committee must approve before you become a member.</p>
              )}
              <button type="button" onClick={accept} style={{ marginTop: 16, background: '#86BE41', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 16px', width: '100%', minHeight: 44 }}>
                Confirm and join
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && step === 'home' && selected && (
        <div>
          <h2 style={{ fontSize: 18 }}>{selected.club.name}</h2>
          <p style={{ fontSize: 14 }}>Pool R{Number(selected.poolBalanceZar || 0).toFixed(2)}</p>
          <p style={{ fontSize: 14 }}>{selected.constitution?.summary}</p>
          <label htmlFor="contrib" style={{ display: 'block', fontSize: 13, marginTop: 16 }}>Contribute now</label>
          <input
            id="contrib"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, marginTop: 6 }}
          />
          <button type="button" onClick={contribute} style={{ marginTop: 12, background: '#86BE41', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 16px', width: '100%' }}>
            Pay from wallet
          </button>

          {selected.vasAdvance?.enabled && (
            <div style={{ marginTop: 24, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
              <h3 style={{ fontSize: 16, margin: 0 }}>Club VAS Advance</h3>
              <p style={{ fontSize: 13, color: '#4b5563' }}>
                {selected.vasAdvance.copy || 'The club advances this airtime or data. MyMoolah is not the lender and charges no credit fee.'}
              </p>
              <p style={{ fontSize: 13 }}>Pot available R{Number(selected.vasAdvance.availableZar || 0).toFixed(2)}</p>
              {selected.vasAdvance.myOutstandingZar > 0 && (
                <div>
                  <p style={{ fontSize: 13 }}>You owe the club R{Number(selected.vasAdvance.myOutstandingZar).toFixed(2)}.</p>
                  <button type="button" onClick={repayAdvance} style={{ marginTop: 8, background: '#2D8CCA', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 14px' }}>
                    Repay from wallet
                  </button>
                </div>
              )}
              {selected.vasAdvance.canRequest && (
                <div>
                  <label htmlFor="vas-kind" style={{ display: 'block', fontSize: 13, marginTop: 12 }}>Product</label>
                  <select
                    id="vas-kind"
                    value={advanceKind}
                    onChange={(e) => setAdvanceKind(e.target.value as 'airtime' | 'data')}
                    style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, marginTop: 6 }}
                  >
                    <option value="airtime">Airtime</option>
                    <option value="data">Data</option>
                  </select>
                  <label htmlFor="vas-amt" style={{ display: 'block', fontSize: 13, marginTop: 12 }}>Amount (R10–R50)</label>
                  <input
                    id="vas-amt"
                    value={advanceAmount}
                    onChange={(e) => setAdvanceAmount(e.target.value)}
                    inputMode="decimal"
                    style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, marginTop: 6 }}
                  />
                  <label htmlFor="vas-msisdn" style={{ display: 'block', fontSize: 13, marginTop: 12 }}>Mobile number</label>
                  <input
                    id="vas-msisdn"
                    value={advanceMsisdn}
                    onChange={(e) => setAdvanceMsisdn(e.target.value)}
                    inputMode="tel"
                    placeholder="0821234567"
                    style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: 10, marginTop: 6 }}
                  />
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 13 }}>
                    <input type="checkbox" checked={ncaAttested} onChange={(e) => setNcaAttested(e.target.checked)} />
                    <span>I confirm the club advances this, not MyMoolah. There is no MyMoolah credit fee. I will repay from my next contribute or wallet.</span>
                  </label>
                  <button type="button" onClick={requestAdvance} style={{ marginTop: 12, background: '#2D8CCA', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 16px', width: '100%' }}>
                    Request club airtime
                  </button>
                </div>
              )}
              {!selected.vasAdvance.canRequest && selected.vasAdvance.blockedReason && selected.vasAdvance.myOutstandingZar <= 0 && (
                <p style={{ fontSize: 13, color: '#6b7280' }}>{selected.vasAdvance.blockedReason}</p>
              )}
            </div>
          )}
        </div>
      )}

      {!loading && step === 'success' && (
        <div>
          <CheckCircle className="w-8 h-8" color="#86BE41" />
          <p style={{ marginTop: 12 }}>{message}</p>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 16, color: '#b45309', display: 'flex', gap: 8 }}>
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
